<template>
  <div class="pb-8">
    <v-card class="mb-6 pa-4" outlined>
      <v-row>
        <v-col cols="12">
          <div class="d-flex align-center mb-2">
            <v-textarea
              v-model="urlList.value"
              label="Liste d'URLs (une par ligne)"
              outlined
              rows="8"
              placeholder="https://example.com/article1
https://example.com/article2
https://example.com/article3"
              hint="Entrez une URL par ligne pour générer des articles"
              persistent-hint
            ></v-textarea>
            <div class="ml-4 d-flex flex-column">
              <v-btn
                color="secondary"
                outlined
                class="mb-2"
                @click="triggerFileInput"
              >
                <v-icon left>mdi-file-excel</v-icon>
                Importer Excel
              </v-btn>
              <input
                ref="fileInput"
                type="file"
                accept=".xlsx,.xls"
                hidden
                @change="handleFileImport"
              />
              <span
                v-if="importedFileName"
                class="caption text-truncate"
                style="max-width: 150px"
              >
                {{ importedFileName }}
              </span>
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="3">
          <site-filter
            siteListGetter="aiFactory/getSiteConnectors"
            itemValue="site.id"
            itemText="site.name"
            store="aiFactory"
            storeGetter="getSelectedSites"
            :multiple="false"
            :dependsOnSiteGroups="false"
          />
        </v-col>
        <v-col cols="3">
          <custom-prompt-template-filter
            :noUrl="true"
            :forceSiteId="selectedSite || 0"
            preselect-default
            storeGetter="getCustomPromptTemplate"
            storeUpdater="updateCustomPromptTemplate"
            store="aiFactory"
            @valueChanged="onTemplateChanged"
          />
        </v-col>
        <v-col cols="3">
          <status-filter
            store="aiFactory"
            storeGetter="getPublicationStatus"
            storeUpdater="updatePublicationStatus"
            :noUrl="true"
          ></status-filter>
        </v-col>
        <v-col cols="3">
          <!-- Lu au clic « Générer » sur un lot de l'historique : c'est la
               qu'est creee la file, le lot lui-meme ne stocke pas l'image. -->
          <aif-image-mode-toggle
            v-model="imageHandler"
            layout="select"
            label="Image"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <schedule-filter
            v-if="selectedStatus === 85"
            :urls="parsedUrls"
            :siteId="selectedSite"
            :templateId="selectedTemplateValue.value"
            @batchCreated="onBatchCreated"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" class="text-right">
          <v-btn
            v-if="selectedStatus === 84"
            color="primary"
            class="mt-3"
            :disabled="buttonDisabled"
            @click.native="submit"
          >
            {{ parsedUrls.length > 1 ? "Créer le batch" : "Créer le batch" }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <planning-history
      v-if="selectedSite"
      ref="planningHistory"
      :siteId="selectedSite"
      :templateId="selectedTemplateValue.value"
      @generate="handleGenerateFromHistory"
    />

    <progress-bar
      title="🤖 Votre agent IA est sur le coup..."
      :showDownloadState="false"
      :download="download"
      :steps="steps"
    />

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      timeout="-1"
      top
      class="snackbar-center"
    >
      <div>
        <strong>{{ snackbar.message }}</strong>
        <div v-if="snackbar.failedCount > 0" class="mt-2">
          <v-chip
            color="error"
            small
            class="cursor-pointer"
            @click="downloadErrorLog"
          >
            <v-icon left small>mdi-download</v-icon>
            ❌ {{ snackbar.failedCount }} erreur(s) - Télécharger les logs
          </v-chip>
        </div>
      </div>

      <template v-slot:action>
        <v-btn
          v-for="action in snackbar.actions"
          :key="action.text"
          color="primary"
          dark
          large
          elevation="2"
          class="ma-2"
          @click="action.onClick()"
        >
          {{ action.text }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { computed, reactive, ref, watch } from "@vue/composition-api";

import * as XLSX from "xlsx";

import ProgressBar, {
  download_initial_state,
  resetDownload,
} from "@/components/common/ProgressBar";
import SiteFilter from "@/components/common/filters/SiteFilter";
import AifImageModeToggle from "@/components/ai-factory/common/AifImageModeToggle.vue";
import CustomPromptTemplateFilter from "@/components/ai-factory/full-auto/filters/CustomPromptTemplateFilter.vue";
import StatusFilter from "../../full-auto/filters/StatusFilter.vue";
import ScheduleFilter from "../filters/ScheduleFilter.vue";
import PlanningHistory from "../PlanningHistory.vue";
import useAxios from "@/hooks/useAxios";
import useProxy from "@/hooks/useProxy";

export default {
  name: "UrlBatch",
  components: {
    AifImageModeToggle,
    ProgressBar,
    SiteFilter,
    CustomPromptTemplateFilter,
    StatusFilter,
    ScheduleFilter,
    PlanningHistory,
  },
  setup(props, { emit }) {
    const { axiosGet, axiosPost, axiosDelete } = useAxios();
    const { route, store, router } = useProxy();

    const snackbar = reactive({
      show: false,
      message: "",
      color: "success",
      actions: [],
      failedCount: 0,
      failedItems: [],
    });

    const urlList = reactive({
      value: "",
    });

    const fileInput = ref(null);
    const importedFileName = ref("");
    const planningHistory = ref(null);

    const imageHandler = computed({
      get: () => store.getters["aiFactory/getGenerationImageHandler"],
      set: (value) =>
        store.dispatch("aiFactory/updateGenerationImageHandler", value),
    });

    const onBatchCreated = () => {
      if (planningHistory.value) {
        planningHistory.value.fetchBatches();
      }
    };

    const triggerFileInput = () => {
      if (fileInput.value) {
        fileInput.value.click();
      }
    };

    const handleFileImport = (event) => {
      const file = event.target.files[0];
      if (!file) return;

      importedFileName.value = file.name;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          const urls = jsonData
            .flat()
            .map((cell) => String(cell).trim())
            .filter((cell) => cell && isValidUrl(cell));

          if (urls.length > 0) {
            urlList.value = urls.join("\n");
          }
        } catch (error) {
          console.error("Error parsing Excel file:", error);
          snackbar.message = "Erreur lors de la lecture du fichier Excel";
          snackbar.color = "error";
          snackbar.show = true;
        }
      };
      reader.readAsArrayBuffer(file);
      event.target.value = "";
    };

    const page = reactive({
      loading: false,
      dataToPublish: [],
    });

    const download = reactive(download_initial_state());

    const parsedUrls = computed(() => {
      if (!urlList.value.trim()) return [];
      const urls = urlList.value
        .split("\n")
        .map((url) => url.trim())
        .filter((url) => url && isValidUrl(url));
      return [...new Set(urls)];
    });

    const hasValidUrls = computed(() => parsedUrls.value.length > 0);

    const isValidUrl = (string) => {
      try {
        const url = new URL(string);
        return url.protocol === "http:" || url.protocol === "https:";
      } catch (_) {
        return false;
      }
    };

    // Dictionnaire pour mapper les IDs de statut vers les valeurs textuelles
    const STATUS_MAP = {
      84: "draft",
      85: "publish",
    };

    const setDataToPublish = (_, data) => {
      page.dataToPublish = data;
      emit("dataToPublishChanged", data);
    };

    const submit = async () => {
      const results = [];
      const selectedSites = store.getters["aiFactory/getSelectedSites"];

      let sources;
      if (scheduleData.schedule?.itemsWithTitles?.length > 0) {
        sources = scheduleData.schedule.itemsWithTitles.map((item) => ({
          url: item.url,
          title: item.title,
        }));
      } else {
        sources = parsedUrls.value.map((url) => ({ url, title: null }));
      }

      const isDraft = selectedStatus.value === 84;

      if (!isDraft) {
        download.running = true;
        download.percentCompleted = 0;
        page.submitting = true;
      }

      if (isDraft) {
        const createResponse = await axiosPost(
          "/news-radar-services/planning_batch",
          {
            site_id: selectedSites,
            aif_custom_prompt_template_id: selectedTemplateValue.value,
            sources,
          }
        );

        const batchId = createResponse.data.id;

        if (planningHistory.value) {
          planningHistory.value.fetchBatches();
        }
        return;
      } else {
        const scheduledList = scheduleData.schedule?.items || [];

        const createResponse = await axiosPost(
          "/news-radar-services/planning_batch",
          {
            site_id: selectedSites,
            aif_custom_prompt_template_id: selectedTemplateValue.value,
            sources,
            scheduled_list: scheduledList,
          }
        );

        if (planningHistory.value) {
          planningHistory.value.fetchBatches();
        }
        return;
      }
    };

    const selectedSite = computed(
      () => store.getters["aiFactory/getSelectedSites"]
    );

    const selectedStatus = computed(() => {
      const status = store.getters["aiFactory/getPublicationStatus"];
      return status;
    });

    const selectedTemplateValue = reactive({
      value: store.getters["aiFactory/getCustomPromptTemplate"],
    });

    const scheduleData = reactive({
      schedule: null,
    });

    const onScheduleChanged = (schedule) => {
      scheduleData.schedule = schedule;
    };

    const onTemplateChanged = (value) => {
      selectedTemplateValue.value = value;
    };

    watch(
      () => store.getters["aiFactory/getCustomPromptTemplate"],
      (value) => {
        selectedTemplateValue.value = value;
      },
      { immediate: true }
    );

    const buttonDisabled = computed(() => {
      // console.log(parsedUrls.value);
      // console.log(selectedSite.value);
      // console.log(selectedTemplateValue.value);
      // console.log(selectedStatus.value);
      // console.log(scheduleData);
      const hasRequiredFields =
        parsedUrls.value.length > 0 &&
        selectedSite.value &&
        selectedTemplateValue.value;

      if (!hasRequiredFields) {
        return true;
      }

      if (selectedStatus.value === 84) {
        return false;
      }

      if (selectedStatus.value === 85 && scheduleData.schedule) {
        if (
          scheduleData.schedule.items &&
          scheduleData.schedule.items.length > 0
        ) {
          return false;
        }
      }

      return true;
    });

    watch(
      () => download.percentCompleted,
      (newValue) => {
        if (newValue >= 100) {
          setTimeout(() => {
            resetDownload(download);
          }, 500);
        }
      }
    );

    const handleGenerateFromHistory = async (batch) => {
      const results = [];
      const selectedSites = store.getters["aiFactory/getSelectedSites"];

      download.running = true;
      download.percentCompleted = 0;
      page.submitting = true;

      const isDraft = !batch.results.some((r) => r.scheduled && r.status !== "failed");
      const status = isDraft ? "draft" : "publish";

      const validResults = batch.results.filter((r) => r.status !== "failed");
      const sources = validResults.map((r) => ({
        url: r.url,
        title: r.title,
      }));
      const scheduledList = isDraft ? [] : validResults
        .filter((r) => r.scheduled)
        .map((r) => r.scheduled);

      console.log("FRONT - scheduledList envoyé:", scheduledList);

      try {
        const response = await axiosPost(
          "/news-radar-services/sources_batch_process",
          {
            site_id: batch.site_id,
            // Allows the backend to store the exact queue association in this batch.
            planning_batch_id: batch.id,
            sources,
            aif_custom_prompt_template_id: batch.aif_custom_prompt_template_id,
            status,
            user_email: localStorage.getItem("userEmail") || null,
            image_handler: imageHandler.value,
            ...(scheduledList.length > 0 && { scheduled_list: scheduledList }),
          },
          {
            onDownloadProgress: (progressEvent) => {
              if (progressEvent.total > 0) {
                download.percentCompleted = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
                );
              }
            },
          }
        );

        if (response.data.success && response.data.success.length > 0) {
          response.data.success.forEach((topic) => {
            results.push({
              topic: topic,
              status: "success",
            });
          });
        }
        const failedItems = Array.isArray(response.data.failed)
          ? response.data.failed
          : [];

        const successCount = results.length;
        const errorCount = failedItems.length;

        snackbar.message = `${successCount} brouillon(s) généré(s) avec succès`;
        snackbar.color = errorCount > 0 ? "error" : "success";
        snackbar.failedCount = errorCount;
        snackbar.failedItems = failedItems;
        snackbar.show = true;

        // if (errorCount === 0) {
        //   await axiosDelete(`/news-radar-services/planning_batch/${batch.id}`);
        // }

        snackbar.actions = [
          {
            text: "OK",
            onClick: () => {
              if (route.name === "ai-factory-post-generation-list") {
                snackbar.show = false;
              } else {
                router.push({
                  name: "ai-factory-post-generation-list",
                  query: selectedSites?.length
                    ? { site_id: selectedSites[0] }
                    : {},
                });
              }
            },
          },
        ];
      } catch (error) {
        console.error("Error:", error);
        snackbar.show = false;
        snackbar.message = "Erreur lors de la génération des brouillons";
        snackbar.color = "error";
        snackbar.failedCount = 0;
        snackbar.failedItems = [];
        snackbar.show = true;
      } finally {
        page.submitting = false;
      }
    };

    const handleViewDetails = (batch) => {
      console.log("View details:", batch);
    };

    const downloadErrorLog = () => {
      const items = snackbar.failedItems || [];
      if (items.length === 0) return;

      const timestamp = new Date().toISOString();
      const lines = [
        `Échec d'envoi en postqueue - ${timestamp}`,
        `Total d'erreurs: ${items.length}`,
        "=".repeat(60),
        "",
      ];
      items.forEach((item, idx) => {
        lines.push(`#${idx + 1}`);
        lines.push(`URL:        ${item.url || "-"}`);
        if (item.topic) lines.push(`Topic:      ${item.topic}`);
        if (item.scheduled) lines.push(`Scheduled:  ${item.scheduled}`);
        lines.push(`Erreur:     ${item.error || "-"}`);
        lines.push("-".repeat(60));
        lines.push("");
      });

      const blob = new Blob([lines.join("\n")], {
        type: "text/plain;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `urlbatch-errors-${timestamp.replace(/[:.]/g, "-")}.log`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    return {
      imageHandler,
      snackbar,
      buttonDisabled,
      download,
      urlList,
      planningHistory,
      hasValidUrls,
      loading: page.loading,
      parsedUrls,
      selectedSite,
      selectedStatus,
      selectedTemplateValue,
      onScheduleChanged,
      onTemplateChanged,
      onBatchCreated,
      dataToPublish: page.dataToPublish,
      setDataToPublish,
      page,
      steps: {
        mdiMerge: "Agrégation des résultats",
        mdiCardSearchOutline: "Analyse des propositions",
        mdiSendClockOutline: "Finalisation de l'envoi",
      },
      submit,
      fileInput,
      importedFileName,
      triggerFileInput,
      handleFileImport,
      handleGenerateFromHistory,
      handleViewDetails,
      downloadErrorLog,
    };
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
